import { Box, IconButton, type SxProps, type Theme } from '@mui/material';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { Children, type ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { useCarouselDotPagination } from '../CarouselDotPagination';
import { HXCIcon } from '../HXCIcon';

const BAR_WIDTH = '5rem';
const BAR_HEIGHT = '0.25rem';
const DEFAULT_AUTOPLAY_DELAY_MS = 5000;

export interface SlideCarouselProps {
  children: ReactNode;
  showIndicators?: boolean;
  activeIndicatorColour?: string;
  inactiveIndicatorColour?: string;
  indicatorSx?: SxProps<Theme>;
  showNavButtons?: boolean;
  autoplay?: boolean;
  autoplayDelay?: number;
  sx?: SxProps<Theme>;
  loop?: boolean;
}

const CarouselBarPagination = ({
  scrollSnaps,
  selectedIndex,
  onDotClick,
  activeColour,
  inactiveColour,
  sx,
}: {
  scrollSnaps: number[];
  selectedIndex: number;
  onDotClick: (index: number) => void;
  activeColour: string;
  inactiveColour: string;
  sx?: SxProps<Theme>;
}) => (
  <Box
    role="tablist"
    aria-label="Carousel pagination"
    sx={{
      display: 'flex',
      justifyContent: 'center',
      gap: '1rem',
      width: '100%',
      ...sx,
    }}
  >
    {scrollSnaps.map((_, index) => {
      const isActive = index === selectedIndex;

      return (
        <Box
          key={index}
          component="button"
          type="button"
          role="tab"
          aria-selected={isActive}
          aria-label={`Go to slide ${index + 1}`}
          onClick={() => onDotClick(index)}
          sx={{
            appearance: 'none',
            WebkitAppearance: 'none',
            border: 'none',
            width: BAR_WIDTH,
            height: BAR_HEIGHT,
            minWidth: 0,
            p: 0,
            cursor: 'pointer',
            bgcolor: isActive ? activeColour : inactiveColour,
            opacity: isActive ? 1 : 0.4,
            borderRadius: 0,
          }}
        />
      );
    })}
  </Box>
);

const navButtonSx = {
  bgcolor: 'silver.main',
  borderRadius: '50%',
  width: '2.75rem',
  height: '2.75rem',
  zIndex: 2,
  '&:hover': {
    bgcolor: 'lightGray.main',
  },
} satisfies SxProps<Theme>;

export const SlideCarousel = ({
  children,
  showIndicators,
  activeIndicatorColour = '#FFF',
  inactiveIndicatorColour = '#FFF',
  indicatorSx,
  showNavButtons = false,
  autoplay = true,
  autoplayDelay = DEFAULT_AUTOPLAY_DELAY_MS,
  sx,
  loop = true,
}: SlideCarouselProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const slides = Children.toArray(children);
  const hasMultipleSlides = slides.length > 1;

  const autoplayPlugin = useMemo(
    () =>
      Autoplay({
        delay: autoplayDelay,
        playOnInit: true,
        // stopOnInteraction must be false when using stopOnMouseEnter — otherwise
        // embla never registers mouseleave and autoplay stays paused after hover.
        stopOnInteraction: false,
        stopOnMouseEnter: true,
        stopOnFocusIn: false,
        rootNode: () => containerRef.current,
      }),
    [autoplayDelay],
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: loop && hasMultipleSlides, align: 'start' },
    autoplay ? [autoplayPlugin] : [],
  );
  const { selectedIndex, scrollSnaps, onDotClick } = useCarouselDotPagination(emblaApi);

  useEffect(() => {
    if (!emblaApi) return;

    emblaApi.reInit({ loop: loop && hasMultipleSlides, align: 'start' });

    if (!autoplay || !hasMultipleSlides) return;

    const autoplayControl = emblaApi.plugins()?.autoplay;
    autoplayControl?.reset();
    autoplayControl?.play();
  }, [emblaApi, autoplay, hasMultipleSlides, loop, slides.length]);

  const handleNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const handlePrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);

  const shouldShowIndicators = (showIndicators ?? slides.length > 1) && scrollSnaps.length > 1;

  return (
    <Box ref={containerRef} sx={{ position: 'relative', width: '100%', ...sx }}>
      {showNavButtons && slides.length > 1 && (
        <>
          <IconButton
            aria-label="Previous slide"
            onClick={handlePrev}
            sx={{
              ...navButtonSx,
              position: 'absolute',
              left: '1rem',
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          >
            <HXCIcon name="arrowLeftRg" color="darkGray" />
          </IconButton>
          <IconButton
            aria-label="Next slide"
            onClick={handleNext}
            sx={{
              ...navButtonSx,
              position: 'absolute',
              right: '1rem',
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          >
            <HXCIcon name="arrowRightRg" color="darkGray" />
          </IconButton>
        </>
      )}

      <Box ref={emblaRef} sx={{ overflow: 'hidden', width: '100%' }}>
        <Box sx={{ display: 'flex' }}>
          {slides.map((slide, index) => (
            <Box key={index} sx={{ flex: '0 0 100%', minWidth: 0 }} aria-roledescription="slide">
              {slide}
            </Box>
          ))}
        </Box>
      </Box>

      {shouldShowIndicators && (
        <CarouselBarPagination
          scrollSnaps={scrollSnaps}
          selectedIndex={selectedIndex}
          onDotClick={onDotClick}
          activeColour={activeIndicatorColour}
          inactiveColour={inactiveIndicatorColour}
          sx={indicatorSx}
        />
      )}
    </Box>
  );
};
